import { useEffect } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { BodyText, Button, HStack, useResponsive } from '@garnish/core';
import { Modal } from '@garnish/overlays';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationConfirmExitModal = (props: Props) => {
  const {
    isVisible,
    title,
    body,
    message,
    confirmButtonTitle,
    cancelButtonTitle,
    target,
    size = 'md',
    onConfirm,
    onCancel,
    onShow,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const formattedMessage = message ?? formatMessage(messages.message);
  const formattedTitle = title
    ? title.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.title, { target });
  const formattedBody = body
    ? body.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.body);
  const formattedConfirmButtonTitle = confirmButtonTitle
    ? confirmButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.returnTo, { target });
  const formattedCancelButtonTitle = cancelButtonTitle
    ? cancelButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.continueCustomization);
  const formattedLeaveButtonTitle = confirmButtonTitle
    ? confirmButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.leave);
  const formattedStayOnPageButtonTitle = cancelButtonTitle
    ? cancelButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.stayOnPage);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  if (size === 'md') {
    return (
      <Modal
        visible={isVisible}
        size="small"
        accessibilityLabel={formatMessage(messages.modalLabel)}
        onRequestClose={onCancel}
      >
        <CustomizationModalContentContainer size={size}>
          <BodyText sizeMatch={['22']} style={styles.message}>
            {formattedMessage}
          </BodyText>

          <SafeAreaView edges={match([['bottom'], undefined])}>
            <HStack itemsPerRow={2} gap={theme.spacing['4']} style={styles.ctaContainerXS}>
              <Button size="large-wide" palette="kale" onPress={onConfirm} style={styles.cta}>
                {formattedLeaveButtonTitle}
              </Button>

              <Button size="large-wide" palette="primary" onPress={onCancel} style={styles.cta}>
                {formattedStayOnPageButtonTitle}
              </Button>
            </HStack>
          </SafeAreaView>
        </CustomizationModalContentContainer>
      </Modal>
    );
  }

  return (
    <Modal
      visible={isVisible}
      size="medium"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onCancel}
    >
      <CustomizationModalContentContainer size={size}>
        <CustomizationModalHeading>{formattedTitle}</CustomizationModalHeading>

        <BodyText sizeMatch={['16']}>{formattedBody}</BodyText>

        <View>
          <HStack itemsPerRow={2} gap={theme.spacing['4']} style={styles.ctaContainerSM}>
            <Button
              size={match(['large', 'large-wide'])}
              palette="secondary"
              onPress={onConfirm}
              style={styles.ctaLG}
            >
              {formattedConfirmButtonTitle}
            </Button>

            <Button
              size={match(['large', 'large-wide'])}
              palette="primary"
              onPress={onCancel}
              style={styles.ctaLG}
            >
              {formattedCancelButtonTitle}
            </Button>
          </HStack>
        </View>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly modalLabel: NoMessageValues;
  readonly title: { readonly target: MessageValue };
  readonly body: NoMessageValues;
  readonly message: NoMessageValues;
  readonly returnTo: { readonly target: MessageValue };
  readonly leave: NoMessageValues;
  readonly continueCustomization: NoMessageValues;
  readonly stayOnPage: NoMessageValues;
}>({
  modalLabel: {
    defaultMessage: 'Confirm exit modal',
    description: 'Customization | Confirm exit modal | Label',
  },
  title: {
    defaultMessage: 'Are you sure you want to return to {target}?',
    description: 'Customization | Confirm exit modal | Heading',
  },
  body: {
    defaultMessage: 'You will lose any customizations you’ve made.',
    description: 'Customization | Confirm exit modal | Body',
  },
  message: {
    defaultMessage: 'Are you sure you want to leave this page? Changes you made will not be saved.',
    description: 'Customization | Confirm exit modal | Message',
  },
  returnTo: {
    defaultMessage: 'Yes, return to {target}',
    description: 'Customization | Confirm exit modal | Return button label',
  },
  leave: {
    defaultMessage: 'Leave',
    description: 'Customization | Confirm exit modal | Leave button label',
  },
  continueCustomization: {
    defaultMessage: 'No, continue to customize',
    description: 'Customization | Confirm exit modal | Continue customization button label',
  },
  stayOnPage: {
    defaultMessage: 'Stay on page',
    description: 'Customization | Confirm exit modal | Stay on page button label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DYNAMIC_TARGET_TEXT = '(target)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  message: {
    marginTop: theme.spacing['4'],
    marginBottom: theme.spacing['2'],
  },
  ctaContainerXS: {
    paddingBottom: Platform.select({
      android: theme.spacing['6'],
      default: 0,
    }),
  },
  ctaContainerSM: {
    paddingTop: theme.spacing['2'],
  },
  cta: {
    paddingHorizontal: theme.spacing['1'],
  },
  ctaLG: {
    paddingHorizontal: theme.spacing['1'],
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  title?: string;
  body?: string;
  message?: string;
  confirmButtonTitle: string | undefined;
  cancelButtonTitle: string | undefined;
  target: string;
  size?: 'md' | 'lg';
  onConfirm: () => void;
  onCancel: () => void;
  onShow?: () => void;
};
